import { getGroupComparator } from '@components/Table/table.utils';
import { TableSizeEnum, TColumnsDef } from '@fabric-ui/components';
import { GroupHeaderRowType, HeadCellType } from '@fabric-ui/components/Table';
import {
  defaultAlertsNewSortBy,
  defaultAlertSortBy,
  defaultExceptionsSortBy,
  defaultIncidentThreatsSortBy,
  defaultSortBy,
  defaultSortByTickets,
  FilterIncidentEntityTypesLabelV2,
  FilterTypesLabelV2,
} from '@fabric-ui/utils';
import { CURRENT_STATUS_FIELD, getHeadCellWithExpended, NESTED_CURRENT_STATUS_FIELD } from '@shared/Tickets/utils';
import { GET_DISCOVERY_SETTINGS_LIST } from '@utils/configurationBannerUtils';
import { TicketsNoDataFoundDefault } from '@utils/ticketsEmptyStates/TicketsNoDataFoundDefault';
import { BucketType } from '@views/Settings/TicketStatuses/statuses.utils';
import { FactorType } from '@views/Tickets/Factors/utils';
import { VulCountBySeveritySummaryConfig } from '@views/Tickets/VulcountBySeveritySummary/utils';
import { RequestDocument } from 'graphql-request';
import { PagePathsValue } from '../context/AvContext.types';
import { SearchContextKeys, SearchContextKeysType } from '../context/SearchContext.types';
import {
  APP_PATHS,
  AppPathsValue,
  FeatureFlags,
  FeatureFlagValue,
  PAGE_PATHS,
  Permission,
  PermissionAppName,
  PermissionCategoriesNames,
  PermissionEntitiesNames,
} from '../types';
import { ScreenType, ScreenTypeType } from '../types/savedViews.types';
import { EntityProjectionName, entityProjectionNames } from './entityProjectionNames';
import { alertsNoDataFound } from './ticketsEmptyStates/AlertsNoDataFound';
import { policyViolationsNoDataFound } from './ticketsEmptyStates/PolicyViolationsNoDataFound';
import { violationTicketsNoDataFound } from './ticketsEmptyStates/ViolationTicketsNoDataFound';

export const ACTIONS_MAP = {
  UPDATE: 'update',
  MERGE: 'merge',
  COMMENT: 'comment',
  OUTEGRATION_CREATE: 'outegration_create',
  OUTEGRATION_SCAN: 'outegration_scan',
  ALERT_TO_INCIDENT: 'alert_to_incident',
  ATTACHMENTS: 'attachments',
  OUTEGRATION_ACT: 'outegration_act',
  EXCEPTIONS_SECTION: 'exceptions-section',
  EXCEPTIONS_FORM: 'exceptions-form',
  SELECT_ALL: 'select-all',
  COPILOT: 'copilot',
};

type ActionsMapType = (typeof ACTIONS_MAP)[keyof typeof ACTIONS_MAP];

export const getActionsPermissionConfig = (
  projectionName: string
): Record<
  string,
  {
    resource: PermissionEntitiesNames;
    category?: PermissionCategoriesNames;
    permission?: Permission;
  }
> => ({
  [ACTIONS_MAP.UPDATE]:
    projectionName === entityViewConfig.Exception.projectionName
      ? {
          category: PermissionAppName.VULNERABILITIES,
          resource: PermissionEntitiesNames.EXCEPTION,
          permission: Permission.UPDATE,
        }
      : { resource: PermissionEntitiesNames.OPT, permission: Permission.UPDATE },
  [ACTIONS_MAP.MERGE]: { resource: PermissionEntitiesNames.OPT, permission: Permission.CREATE },
  [ACTIONS_MAP.COMMENT]:
    projectionName === entityViewConfig.Exception.projectionName
      ? {
          category: PermissionAppName.VULNERABILITIES,
          resource: PermissionEntitiesNames.EXCEPTION,
          permission: Permission.UPDATE,
        }
      : { resource: PermissionEntitiesNames.OPT, permission: Permission.UPDATE },
  [ACTIONS_MAP.OUTEGRATION_CREATE]:
    projectionName === entityViewConfig.Exception.projectionName
      ? {
          category: PermissionAppName.VULNERABILITIES,
          resource: PermissionEntitiesNames.EXCEPTION,
          permission: Permission.CREATE,
        }
      : { resource: PermissionEntitiesNames.INT, permission: Permission.CREATE },

  [ACTIONS_MAP.OUTEGRATION_SCAN]: { resource: PermissionEntitiesNames.INT, permission: Permission.CREATE },
  [ACTIONS_MAP.ALERT_TO_INCIDENT]: { resource: PermissionEntitiesNames.OPT, permission: Permission.UPDATE },
  [ACTIONS_MAP.ATTACHMENTS]:
    projectionName === entityViewConfig.Exception.projectionName
      ? {
          category: PermissionAppName.VULNERABILITIES,
          resource: PermissionEntitiesNames.EXCEPTION,
          permission: Permission.UPDATE,
        }
      : { resource: PermissionEntitiesNames.OPT, permission: Permission.UPDATE },
  [ACTIONS_MAP.OUTEGRATION_ACT]: { resource: PermissionEntitiesNames.INT, permission: Permission.CREATE },
  [ACTIONS_MAP.EXCEPTIONS_SECTION]: {
    category: PermissionAppName.VULNERABILITIES,
    resource: PermissionEntitiesNames.EXCEPTION,
    permission: Permission.READ,
  },
  [ACTIONS_MAP.EXCEPTIONS_FORM]: {
    category: PermissionAppName.VULNERABILITIES,
    resource: PermissionEntitiesNames.EXCEPTION,
    permission: Permission.CREATE,
  },
  [ACTIONS_MAP.COPILOT]: { resource: PermissionEntitiesNames.OPT, permission: Permission.UPDATE },
});

export type EntityConfigType = {
  path: PagePathsValue;
  entityTypeId: string;
  projectionName: EntityProjectionName;
  pageTitle: string;
  appPath: AppPathsValue;
  app: string;
  viewScreenType?: ScreenTypeType;
  searchConfig: {
    contextKey: SearchContextKeysType;
    placeholder?: string;
    mode?: TicketsSearchMode;
    fields?: string[];
  };
  defaultSortBy: (alias: string) => any | void;
  titleFieldName?: string;
  labelsV2?: Record<string, string>;
  defaultFilters?: Record<string, any[]>;
  showVisualEntityExplorer?: boolean;
  defaultSavedView?: string;
  vulnsField?: string;
  extraFilters?: string[];
  hideCommentsTab?: boolean;
  hideActivityTab?: boolean;
  hideGroupBy?: boolean;
  hideApplyChanges?: boolean;
  groupingKeyField?: string;
  extraRelationGroups?: string[];
  isTicketFilters?: boolean;
  rowGroupByField?: (row: any) => any;
  getHeadCellFunc?: (hc: HeadCellType[] | TColumnsDef, isTableV2?: boolean) => HeadCellType[] | TColumnsDef;
  getData?: (hc: any) => any;
  actions: {
    grid?: Record<ActionsMapType, boolean>;
    panel?: Record<ActionsMapType, boolean>;
  };
  styles?: {
    isTransparentPageBackground?: boolean;
    tableSize?: TableSizeEnum;
  };
  NoDataComponent?: React.ComponentType<{
    projDisplayName: string;
    isRefetching: boolean;
    clearAllFilters: (keepView?: boolean, keepHeadCells?: boolean) => void;
  }>;
  isMultiSelect?: boolean;
  disablePagination?: boolean;
  currentStatusName?: typeof CURRENT_STATUS_FIELD | typeof NESTED_CURRENT_STATUS_FIELD;
  filterBucketsFunc?: (bucket: BucketType, currentActiveBucket?: BucketType) => boolean;
  showAllFieldsToggle?: boolean;
  vulCountBySeveritySummaryConfig?: VulCountBySeveritySummaryConfig;
  configurationBanner?: {
    query: RequestDocument;
    configureLink: { app: AppPathsValue; path: PagePathsValue };
    title: string;
    description: string;
    ffForShowing?: FeatureFlagValue;
    select?: (data: any) => boolean;
  };
  showDiscoveryChain?: boolean;
};

type IEntityViewConfig = {
  [key in EntityTypeConfigKeys]: EntityConfigType;
};

export enum EntityTypeConfigKeys {
  Ticket = 'Ticket',
  Asset = 'Asset',
  Incident = 'Incident',
  IncidentThreats = 'IncidentThreats',
  Alert = 'Alert',
  AlertNew = 'AlertNew',
  Finding = 'Finding',
  Factor = 'Factor',
  PolicyViolation = 'PolicyViolation',
  Exception = 'Exception',
  ViolationTicket = 'ViolationTicket',
  IdentityFinding = 'IdentityFinding',
  User = 'User',
  Tenant = 'Tenant',
}

export type TicketsSearchMode = 'DEFAULT' | 'FULL_WIDTH';

export const entityViewConfig: IEntityViewConfig = {
  Ticket: {
    path: PAGE_PATHS.TICKETS,
    entityTypeId: 'Ticket',
    projectionName: entityProjectionNames.Ticket,
    pageTitle: 'Tickets',
    appPath: APP_PATHS.VULNERABILITIES,
    app: 'Vulnerabilities',
    viewScreenType: ScreenType.Tickets,
    labelsV2: FilterTypesLabelV2,
    showVisualEntityExplorer: true,
    searchConfig: {
      placeholder: 'Search Title',
      fields: ['ticket.title', 'ticket._key'],
      contextKey: SearchContextKeys.Tickets,
    },
    defaultSavedView: 'Active',
    vulnsField: 'ticket.finding_count',
    extraFilters: ["ticket.state != 'ARCHIVED'"],
    groupingKeyField: 'finding.grouping_key',
    titleFieldName: 'title',
    extraRelationGroups: ['Assignee'],
    isTicketFilters: true,
    defaultSortBy: alias => defaultSortByTickets(alias),
    actions: {
      grid: {
        [ACTIONS_MAP.UPDATE]: true,
        [ACTIONS_MAP.MERGE]: true,
        [ACTIONS_MAP.COMMENT]: true,
        [ACTIONS_MAP.OUTEGRATION_CREATE]: true,
        [ACTIONS_MAP.OUTEGRATION_ACT]: true,
        [ACTIONS_MAP.SELECT_ALL]: true,
      },
      panel: {
        [ACTIONS_MAP.ATTACHMENTS]: true,
        [ACTIONS_MAP.EXCEPTIONS_SECTION]: true,
        [ACTIONS_MAP.EXCEPTIONS_FORM]: true,
        [ACTIONS_MAP.COPILOT]: true,
      },
    },
    styles: {
      isTransparentPageBackground: true,
    },
    NoDataComponent: TicketsNoDataFoundDefault,
    currentStatusName: NESTED_CURRENT_STATUS_FIELD,
    vulCountBySeveritySummaryConfig: {
      dims: {
        LOW: 'low_active_findings_risk_mass_granular',
        MEDIUM: 'medium_active_findings_risk_mass_granular',
        HIGH: 'high_active_findings_risk_mass_granular',
        CRITICAL: 'critical_active_findings_risk_mass_granular',
        TOTAL: 'active_findings_risk_mass_granular',
      },
      label: 'Risk Mass',
    },
  },
  Asset: {
    path: PAGE_PATHS.ASSETS,
    entityTypeId: 'Asset',
    projectionName: entityProjectionNames.Asset,
    pageTitle: 'Assets',
    appPath: APP_PATHS.VULNERABILITIES,
    app: 'Vulnerabilities',
    viewScreenType: ScreenType.AssetView,
    showVisualEntityExplorer: true,
    labelsV2: FilterTypesLabelV2,
    defaultFilters: {
      'asset.source_names': [],
      'asset.type': [],
      'asset.site': [],
      'asset.os': [],
    },
    searchConfig: {
      fields: ['asset.name', 'asset._key'],
      contextKey: SearchContextKeys.Assets,
    },
    defaultSavedView: 'Active',
    vulnsField: '',
    extraFilters: ["asset.state != 'ARCHIVED'"],
    hideCommentsTab: true,
    showAllFieldsToggle: true,
    extraRelationGroups: ['Owner', 'Operating System'],
    titleFieldName: 'name',
    defaultSortBy,
    actions: {
      grid: {
        [ACTIONS_MAP.UPDATE]: true,
        // Added to support Act button [AVA-9992] fixed in [AVA-10084]
        [ACTIONS_MAP.OUTEGRATION_ACT]: true,
        [ACTIONS_MAP.SELECT_ALL]: true,
        [ACTIONS_MAP.OUTEGRATION_SCAN]: true,
      },
      panel: { [ACTIONS_MAP.ATTACHMENTS]: false },
    },
    styles: {
      isTransparentPageBackground: true,
    },
    NoDataComponent: TicketsNoDataFoundDefault,
    vulCountBySeveritySummaryConfig: {
      dims: {
        LOW: 'low_active_findings_risk_mass_granular',
        MEDIUM: 'medium_active_findings_risk_mass_granular',
        HIGH: 'high_active_findings_risk_mass_granular',
        CRITICAL: 'critical_active_findings_risk_mass_granular',
        TOTAL: 'active_findings_risk_mass_granular',
      },
      label: 'Risk Mass',
    },
    configurationBanner: {
      query: GET_DISCOVERY_SETTINGS_LIST,
      configureLink: { app: APP_PATHS.PLATFORM, path: PAGE_PATHS.DISCOVERY_SETTINGS },
      title: 'External Attack Surface Scanning',
      description: 'Scan and monitor your external attack surface by scanning you domains and integrating results into your inventory',
      ffForShowing: FeatureFlags.ShowAssetDiscoveryPage,
      select: data => data.length > 0,
    },
    showDiscoveryChain: true,
  },
  Incident: {
    path: PAGE_PATHS.INCIDENTS,
    entityTypeId: 'Incident',
    projectionName: entityProjectionNames.Incident,
    pageTitle: 'Incidents',
    app: 'Incidents',
    appPath: APP_PATHS.DETECTIONS,
    viewScreenType: ScreenType.Incidents,
    labelsV2: FilterIncidentEntityTypesLabelV2,
    showVisualEntityExplorer: true,
    searchConfig: {
      fields: ['incident.title', 'incident._key'],
      contextKey: SearchContextKeys.Incidents,
    },
    defaultSavedView: 'Open Tickets',
    groupingKeyField: 'alert.grouping_key',
    titleFieldName: 'title',
    vulnsField: 'incident.alert_count',
    extraFilters: ["incident.state NOT IN ('INACTIVE', 'ARCHIVED')"],
    isTicketFilters: true,
    defaultSortBy,
    actions: {
      grid: {
        [ACTIONS_MAP.UPDATE]: true,
        [ACTIONS_MAP.MERGE]: true,
        [ACTIONS_MAP.COMMENT]: true,
        [ACTIONS_MAP.OUTEGRATION_CREATE]: true,
        [ACTIONS_MAP.OUTEGRATION_ACT]: true,
        [ACTIONS_MAP.SELECT_ALL]: true,
      },
      panel: { [ACTIONS_MAP.ATTACHMENTS]: true },
    },
    styles: {
      isTransparentPageBackground: true,
    },
    NoDataComponent: TicketsNoDataFoundDefault,
    currentStatusName: NESTED_CURRENT_STATUS_FIELD,
  },
  IncidentThreats: {
    path: PAGE_PATHS.INCIDENTS,
    entityTypeId: 'Incident',
    projectionName: entityProjectionNames.Incident,
    pageTitle: 'Incidents',
    app: 'Threats',
    appPath: APP_PATHS.THREATS,
    viewScreenType: ScreenType.IncidentsThreats,
    labelsV2: FilterIncidentEntityTypesLabelV2,
    showVisualEntityExplorer: true,
    searchConfig: {
      fields: ['incident.title', 'incident._key'],
      contextKey: SearchContextKeys.Incidents,
    },
    defaultSavedView: 'Open Tickets',
    titleFieldName: 'title',
    vulnsField: 'incident.alert_count',
    extraFilters: ["incident.state NOT IN ('INACTIVE', 'ARCHIVED')"],
    isTicketFilters: true,
    defaultSortBy: defaultIncidentThreatsSortBy,
    actions: {
      grid: {
        [ACTIONS_MAP.UPDATE]: true,
        [ACTIONS_MAP.MERGE]: true,
        [ACTIONS_MAP.COMMENT]: true,
        [ACTIONS_MAP.OUTEGRATION_CREATE]: true,
        [ACTIONS_MAP.OUTEGRATION_ACT]: true,
        [ACTIONS_MAP.SELECT_ALL]: true,
      },
      panel: { [ACTIONS_MAP.ATTACHMENTS]: true },
    },
    styles: {
      isTransparentPageBackground: true,
    },
    NoDataComponent: TicketsNoDataFoundDefault,
    currentStatusName: NESTED_CURRENT_STATUS_FIELD,
  },
  Alert: {
    path: PAGE_PATHS.ALERTS,
    entityTypeId: 'Alert',
    projectionName: entityProjectionNames.Alert,
    pageTitle: 'Alerts',
    app: 'Incidents',
    appPath: APP_PATHS.DETECTIONS,
    viewScreenType: ScreenType.Alerts,
    showVisualEntityExplorer: true,
    labelsV2: FilterIncidentEntityTypesLabelV2,
    searchConfig: {
      fields: ['alert.title', 'alert._key'],
      contextKey: SearchContextKeys.Alerts,
    },
    defaultSavedView: 'Active',
    titleFieldName: 'rule_name',
    defaultFilters: {
      'alert.severity': ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'],
      'alert.state': [],
      'alert.created': [],
      'alert.tactic_name': [],
      'alert.technique_name': [],
    },
    hideCommentsTab: true,
    hideActivityTab: true,
    vulnsField: '',
    defaultSortBy: defaultAlertSortBy,
    actions: {
      grid: {
        [ACTIONS_MAP.UPDATE]: true,
        [ACTIONS_MAP.ALERT_TO_INCIDENT]: true,
        [ACTIONS_MAP.SELECT_ALL]: true,
      },
      panel: {
        [ACTIONS_MAP.ATTACHMENTS]: false,
      },
    },
    styles: {
      isTransparentPageBackground: false,
      tableSize: TableSizeEnum.Medium,
    },
    NoDataComponent: alertsNoDataFound,
  },
  AlertNew: {
    path: PAGE_PATHS.ALERTS,
    entityTypeId: 'Alert',
    projectionName: entityProjectionNames.Alert,
    pageTitle: 'Alerts',
    app: 'Threats',
    appPath: APP_PATHS.THREATS,
    viewScreenType: ScreenType.AlertsThreats,
    showVisualEntityExplorer: true,
    labelsV2: FilterIncidentEntityTypesLabelV2,
    searchConfig: {
      fields: ['alert.title', 'alert._key'],
      contextKey: SearchContextKeys.AlertsNew,
    },
    defaultSavedView: 'Active',
    titleFieldName: 'title',
    defaultFilters: {
      'alert.state': [],
      'alert.created': [],
      'mitre_attack.technique': [],
      'mitre_attack.tactic': [],
    },
    hideCommentsTab: true,
    hideActivityTab: true,
    vulnsField: '',
    defaultSortBy: defaultAlertsNewSortBy,
    actions: {
      grid: {
        [ACTIONS_MAP.UPDATE]: true,
        [ACTIONS_MAP.ALERT_TO_INCIDENT]: true,
        [ACTIONS_MAP.SELECT_ALL]: true,
      },
      panel: {
        [ACTIONS_MAP.ATTACHMENTS]: false,
      },
    },
    styles: {
      isTransparentPageBackground: false,
      tableSize: TableSizeEnum.Medium,
    },
    NoDataComponent: alertsNoDataFound,
    currentStatusName: CURRENT_STATUS_FIELD,
  },
  Finding: {
    path: PAGE_PATHS.FINDINGS,
    entityTypeId: 'Finding',
    projectionName: entityProjectionNames.Finding,
    pageTitle: 'Findings',
    appPath: APP_PATHS.VULNERABILITIES,
    app: 'Vulnerabilities',
    viewScreenType: ScreenType.FindingsView,
    showVisualEntityExplorer: true,
    labelsV2: FilterTypesLabelV2,
    searchConfig: {
      placeholder: 'Search for CVE or Keyword, Example: CVE-2021-44228...',
      fields: ['finding.title', 'finding.cve_id', 'component.name', 'component._key', 'asset.os', 'finding._key'],
      mode: 'FULL_WIDTH',
      contextKey: SearchContextKeys.Findings,
    },
    defaultSavedView: 'Active Critical and High Findings',
    defaultFilters: {
      'finding.source_names': [],
      'finding.type': [],
      'finding.severity': [],
    },
    extraFilters: ["finding.state != 'ARCHIVED'"],
    extraRelationGroups: ['*'],
    titleFieldName: 'title',
    defaultSortBy,
    hideCommentsTab: true,
    actions: {
      grid: { [ACTIONS_MAP.UPDATE]: true, [ACTIONS_MAP.MERGE]: true, [ACTIONS_MAP.SELECT_ALL]: true },
      panel: { [ACTIONS_MAP.ATTACHMENTS]: false },
    },
    styles: {
      isTransparentPageBackground: false,
      tableSize: TableSizeEnum.Medium,
    },
    NoDataComponent: TicketsNoDataFoundDefault,
  },
  Factor: {
    path: PAGE_PATHS.FACTOR,
    entityTypeId: 'Factor',
    projectionName: entityProjectionNames.Factor,
    pageTitle: 'Factors',
    isMultiSelect: false,
    appPath: APP_PATHS.RISK360,
    app: 'Risk360',
    labelsV2: FilterTypesLabelV2, //--------
    searchConfig: {
      fields: ['factor.name', 'factor.category_name', 'factor._key'],
      contextKey: SearchContextKeys.Factor,
    },
    defaultFilters: {
      'factor.name': [],
      'factor.category_name': [],
      'factor.enabled': [],
    },
    disablePagination: true,
    extraFilters: [],
    rowGroupByField: row => row?.groupId,
    getHeadCellFunc: hc => getHeadCellWithExpended({ headCells: hc }),
    getData: queryData =>
      queryData
        .map(row => ({
          ...row,
          // last30Days: factorLineChartData,
          disableStickyRow: true,
          groupHeaderType:
            row.type === FactorType.GROUP_FACTOR
              ? GroupHeaderRowType.GROUP
              : row.type === FactorType.STANDALONE_FACTOR
                ? GroupHeaderRowType.STANDALONE
                : GroupHeaderRowType.SUB_GROUP,
        }))
        .toSorted(getGroupComparator(row => row?.groupId, 'asc')),
    extraRelationGroups: ['*'],
    titleFieldName: 'name',
    defaultSortBy,
    hideCommentsTab: true,
    hideActivityTab: true,
    hideGroupBy: true,
    actions: {
      grid: { [ACTIONS_MAP.UPDATE]: false, [ACTIONS_MAP.MERGE]: false, [ACTIONS_MAP.SELECT_ALL]: true },
      panel: { [ACTIONS_MAP.ATTACHMENTS]: false },
    },
    styles: {
      isTransparentPageBackground: true,
    },
    NoDataComponent: TicketsNoDataFoundDefault,
  },
  PolicyViolation: {
    path: PAGE_PATHS.POLICY_VIOLATIONS,
    entityTypeId: 'PolicyViolation',
    projectionName: entityProjectionNames.PolicyViolation,
    pageTitle: 'Policy Violations',
    isMultiSelect: true,
    appPath: APP_PATHS.ASSETS,
    app: 'Assets',
    defaultSavedView: 'Active Violations',
    viewScreenType: ScreenType.PolicyViolations,
    labelsV2: FilterTypesLabelV2,
    searchConfig: {
      placeholder: 'Search Policy Violation',
      fields: ['policy_violation.title', 'policy_violation._key'],
      contextKey: SearchContextKeys.PolicyViolation,
    },
    defaultFilters: {
      'policy_violation.state': [],
      'policy_violation.severity': [],
      'policy_violation.policy_name': [],
    },
    disablePagination: false,
    extraRelationGroups: ['*'],
    titleFieldName: 'title',
    defaultSortBy,
    hideCommentsTab: true,
    hideActivityTab: true,
    hideGroupBy: false,
    actions: {
      grid: {
        [ACTIONS_MAP.UPDATE]: true,
        [ACTIONS_MAP.MERGE]: false,
        [ACTIONS_MAP.OUTEGRATION_CREATE]: true,
        [ACTIONS_MAP.OUTEGRATION_ACT]: true,
        [ACTIONS_MAP.SELECT_ALL]: true,
      },
      panel: { [ACTIONS_MAP.ATTACHMENTS]: false },
    },
    styles: {
      isTransparentPageBackground: false,
      tableSize: TableSizeEnum.Medium,
    },
    NoDataComponent: policyViolationsNoDataFound,
  },
  Exception: {
    pageTitle: 'Exceptions',
    path: PAGE_PATHS.EXCEPTIONS,
    entityTypeId: 'Exception',
    projectionName: entityProjectionNames.Exception,
    app: 'Vulnerabilities',
    hideApplyChanges: true,
    appPath: APP_PATHS.VULNERABILITIES,
    viewScreenType: ScreenType.ExceptionsView,
    searchConfig: {
      contextKey: SearchContextKeys.Exceptions,
      fields: ['ticket.title', 'exception._key'],
    },
    groupingKeyField: 'ticket._key',
    extraFilters: ["exception.state != 'ARCHIVED'"],
    defaultSortBy: defaultExceptionsSortBy,
    defaultFilters: {
      'exception.current_status': [],
      'ticket.severity': [],
      'requester.name': [],
    },
    actions: {
      grid: {
        [ACTIONS_MAP.UPDATE]: true,
        [ACTIONS_MAP.COMMENT]: true,
        [ACTIONS_MAP.OUTEGRATION_CREATE]: true,
        [ACTIONS_MAP.SELECT_ALL]: true,
      },
      panel: { [ACTIONS_MAP.OUTEGRATION_CREATE]: true, [ACTIONS_MAP.ATTACHMENTS]: true },
    },
    styles: {
      isTransparentPageBackground: true,
    },
    NoDataComponent: TicketsNoDataFoundDefault,
    currentStatusName: CURRENT_STATUS_FIELD,
    filterBucketsFunc: (bucket, currentActiveBucket) => (currentActiveBucket?.approved ? false : !bucket.closed && !bucket.approved),
  },
  ViolationTicket: {
    path: PAGE_PATHS.VIOLATION_TICKETS,
    entityTypeId: 'ViolationTicket',
    projectionName: entityProjectionNames.ViolationTicket,
    pageTitle: 'Violation Tickets',
    isMultiSelect: true,
    appPath: APP_PATHS.ASSETS,
    app: 'Assets',
    defaultSavedView: 'Active',
    viewScreenType: ScreenType.ViolationTicket,
    labelsV2: FilterTypesLabelV2,
    searchConfig: {
      placeholder: 'Search Violation Tickets',
      fields: ['violation_ticket.title', 'violation_ticket._key'],
      contextKey: SearchContextKeys.ViolationTicket,
    },
    defaultFilters: {
      'violation_ticket.severity': [],
      'violation_ticket.current_status': [],
    },
    disablePagination: false,
    extraRelationGroups: ['Assignee'],
    titleFieldName: 'title',
    defaultSortBy,
    hideCommentsTab: false,
    hideGroupBy: false,
    actions: {
      grid: {
        [ACTIONS_MAP.UPDATE]: true,
        [ACTIONS_MAP.MERGE]: true,
        [ACTIONS_MAP.COMMENT]: true,
        [ACTIONS_MAP.OUTEGRATION_CREATE]: true,
        [ACTIONS_MAP.OUTEGRATION_ACT]: true,
        [ACTIONS_MAP.SELECT_ALL]: true,
      },
      panel: {
        [ACTIONS_MAP.ATTACHMENTS]: true,
      },
    },
    styles: {
      isTransparentPageBackground: true,
    },
    NoDataComponent: violationTicketsNoDataFound,
    currentStatusName: CURRENT_STATUS_FIELD,
  },
  IdentityFinding: {
    path: PAGE_PATHS.IDENTITY_FINDINGS,
    entityTypeId: 'IdentityFinding',
    projectionName: 'uber_identity_findings',
    pageTitle: 'Identity Findings',
    appPath: APP_PATHS.IDENTITIES,
    viewScreenType: ScreenType.IdentityFindingsView,
    app: 'Identities',
    searchConfig: {
      fields: ['identity_finding.title', 'identity_finding._key'],
      mode: 'FULL_WIDTH',
      placeholder: 'Search for Identity Findings or Keywords, Example: Admin without MFA"',
      contextKey: SearchContextKeys.IdentityFindings,
    },
    defaultFilters: {
      'identity_finding.title': [],
      'tenant.name': [],
    },
    defaultSavedView: 'Active',
    vulnsField: '',
    hideCommentsTab: true,
    hideActivityTab: true,
    titleFieldName: 'title',
    defaultSortBy,
    actions: {},
    styles: {
      isTransparentPageBackground: false,
      tableSize: TableSizeEnum.Medium,
    },
    NoDataComponent: TicketsNoDataFoundDefault,
    hideApplyChanges: true,
    isMultiSelect: false,
  },
  User: {
    path: PAGE_PATHS.USERS,
    entityTypeId: 'User',
    projectionName: entityProjectionNames.Identity,
    viewScreenType: ScreenType.UsersView,
    pageTitle: 'Users',
    appPath: APP_PATHS.IDENTITIES,
    app: 'Identities',
    labelsV2: FilterTypesLabelV2,
    defaultFilters: {
      'user.user_name': [],
      'user.type': [],
      'user.identity_type': [],
      'user.source_names': [],
    },
    searchConfig: {
      fields: ['user.user_name', 'user._key'],
      contextKey: SearchContextKeys.Identities,
    },
    defaultSavedView: 'Active',
    vulnsField: '',
    hideCommentsTab: true,
    hideActivityTab: true,
    titleFieldName: 'user_name',
    defaultSortBy: () => [
      {
        property: 'user.email',
        isAsc: true,
      },
    ],
    actions: {},
    styles: {
      isTransparentPageBackground: true,
    },
    NoDataComponent: TicketsNoDataFoundDefault,
    vulCountBySeveritySummaryConfig: {
      dims: {
        LOW: 'low_active_identity_findings',
        MEDIUM: 'medium_active_identity_findings',
        HIGH: 'high_active_identity_findings',
        CRITICAL: 'critical_active_identity_findings',
        TOTAL: 'total_identity_findings',
      },
      label: 'Identity Findings',
    },
    hideApplyChanges: true,
    isMultiSelect: false,
  },
  Tenant: {
    path: PAGE_PATHS.TENANTS,
    entityTypeId: 'Tenant',
    projectionName: 'uber_tenants',
    pageTitle: 'Tenants',
    appPath: APP_PATHS.IDENTITIES,
    viewScreenType: ScreenType.TenantsView,
    app: 'Identities',
    labelsV2: FilterTypesLabelV2,
    searchConfig: {
      fields: ['tenant.user_name', 'tenant._key'],
      contextKey: SearchContextKeys.Tenants,
    },
    defaultSavedView: 'Active',
    vulnsField: '',
    hideCommentsTab: true,
    hideActivityTab: true,
    titleFieldName: 'name',
    defaultSortBy,
    defaultFilters: {
      'tenant.user_name': [],
      'tenant.source_names': [],
    },
    actions: {},
    styles: {
      isTransparentPageBackground: true,
    },
    NoDataComponent: TicketsNoDataFoundDefault,
    vulCountBySeveritySummaryConfig: {
      dims: {
        LOW: 'low_active_identity_findings',
        MEDIUM: 'medium_active_identity_findings',
        HIGH: 'high_active_identity_findings',
        CRITICAL: 'critical_active_identity_findings',
        TOTAL: 'total_identity_findings',
      },
      label: 'Identity Findings',
    },
    hideApplyChanges: true,
    isMultiSelect: false,
  },
};
